import { Box, Card, CircularProgress, Grid, Typography } from '@mui/material';
import {
  ApiResponse,
  DashboardData,
} from 'modules/vulnerabilities/components/DashCard';
import VulnerabilityCharts from 'modules/vulnerabilities/components/VulnerabilityCharts';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { VulnerabilityService } from 'services/VulnerabilityService';

const Vulnerability = (): ReactElement => {
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    counts: { total: 0, open: 0, critical: 0 },
    statusWise: [],
    riskRatingWise: [],
    remediationDeadlines: [],
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchDashboardData = useCallback((): void => {
    setIsLoading(true);
    VulnerabilityService.getDashboard()
      .then(({ data: { data } }: { data: ApiResponse }) => {
        setDashboardData({
          counts: data.counts || { total: 0, open: 0, critical: 0 },
          statusWise: data.statusWise || [],
          riskRatingWise: data.riskRatingWise || [],
          remediationDeadlines: data.remediationDeadlines || [],
        });
      })
      .catch((error) => {
        console.error('Failed to fetch dashboard data:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const totalVulnerabilities = dashboardData.counts.total;
  const openVulnerabilities = dashboardData.counts.open;
  const criticalVulnerabilities = dashboardData.counts.critical;

  if (isLoading) {
    return (
      <Box className="empty-content">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={4}>
        <Card
          variant="outlined"
          sx={{
            padding: '16px',
            borderRadius: '12px',
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography>Total Vulnerabilities</Typography>
          </Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              color: '#3D3C42',
              fontSize: '24px',
            }}
          >
            {totalVulnerabilities.toString()}
          </Typography>
        </Card>
      </Grid>

      <Grid item xs={12} sm={4}>
        <Card
          variant="outlined"
          sx={{
            padding: '16px',
            borderRadius: '12px',
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography>Open Vulnerabilities</Typography>
          </Box>
          <Typography variant="h3">{openVulnerabilities.toString()}</Typography>
        </Card>
      </Grid>

      <Grid item xs={12} sm={4}>
        <Card
          variant="outlined"
          sx={{
            padding: '16px',
            borderRadius: '12px',
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography>Critical Vulnerabilities</Typography>
          </Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              color: '#3D3C42',
              fontSize: '24px',
            }}
          >
            {criticalVulnerabilities.toString()}
          </Typography>
        </Card>
      </Grid>

      <VulnerabilityCharts data={dashboardData} loading={isLoading} />
    </Grid>
  );
};

export default Vulnerability;
